/**
 * Return Module Helpers
 * Shared utilities for Return sub-modules: GRN Return, Issue Return, Receive Return, Expired Return, Sales Return
 */

import {
  TabColumnConfig,
  createSkeletonColumnWidths,
} from '../../utils/skeletonHelpers';

// ============================================================================
// GRN RETURN SKELETON CONFIG
// ============================================================================

/**
 * Base column widths for GRN Return table:
 * return_no, date, clinic_store, supplier, return_by, net_amount
 */
const GRN_RETURN_BASE_WIDTHS = [140, 100, 160, 160, 140, 120];

/**
 * Tab-specific column configurations for GRN Return skeleton
 * 
 * Column structure varies by tab:
 * - All: return_no, date, clinic_store, supplier, return_by, net_amount, status, remarks, actions
 * - Pending: checkbox, return_no, date, clinic_store, supplier, return_by, net_amount, remarks, actions
 * - Others: return_no, date, clinic_store, supplier, return_by, net_amount, remarks, actions
 */
const GRN_RETURN_TAB_CONFIG: TabColumnConfig = {
  // return_no, date, clinic_store, supplier, return_by, net_amount, status, remarks, actions
  'All': [...GRN_RETURN_BASE_WIDTHS, 100, 150, 100],
  // checkbox, return_no, date, clinic_store, supplier, return_by, net_amount, remarks, actions
  'Pending': [...GRN_RETURN_BASE_WIDTHS, 150, 100],
  // return_no, date, clinic_store, supplier, return_by, net_amount, remarks, actions
  'Draft': [...GRN_RETURN_BASE_WIDTHS, 150, 100],
  'Approved': [...GRN_RETURN_BASE_WIDTHS, 150, 100],
  'Rejected': [...GRN_RETURN_BASE_WIDTHS, 150, 100],
  'Cancelled': [...GRN_RETURN_BASE_WIDTHS, 150, 100],
};

/**
 * Default column widths for GRN Return table when tab not found in config
 */
const GRN_RETURN_DEFAULT_WIDTHS = [...GRN_RETURN_BASE_WIDTHS, 150, 100];

/**
 * Get skeleton column widths for GRN Return table
 * 
 * @param selectedTab - Currently selected tab (All, Pending, Draft, etc.)
 * @param hasCheckbox - Whether the table has a checkbox column (true for Pending tab)
 * @returns Array of column widths for skeleton rendering
 */
export const getGRNReturnSkeletonColumnWidths = createSkeletonColumnWidths(
  GRN_RETURN_TAB_CONFIG,
  GRN_RETURN_DEFAULT_WIDTHS
);

// ============================================================================
// ISSUE RETURN SKELETON CONFIG
// ============================================================================

/**
 * Base column widths for Issue Return table:
 * issue_return_no, date, from_clinic_store, to_clinic_store, total_amount
 */
const ISSUE_RETURN_BASE_WIDTHS = [140, 100, 160, 160, 120];

/**
 * Tab-specific column configurations for Issue Return skeleton
 * 
 * Column structure:
 * - All: issue_return_no, date, from_clinic_store, to_clinic_store, total_amount, status, remark, actions
 * - Others: issue_return_no, date, from_clinic_store, to_clinic_store, total_amount, remark, actions
 */
const ISSUE_RETURN_TAB_CONFIG: TabColumnConfig = {
  // issue_return_no, date, from_clinic_store, to_clinic_store, total_amount, status, remark, actions
  'All': [...ISSUE_RETURN_BASE_WIDTHS, 100, 150, 100],
  // issue_return_no, date, from_clinic_store, to_clinic_store, total_amount, remark, actions
  'Draft': [...ISSUE_RETURN_BASE_WIDTHS, 150, 100],
  'Pending': [...ISSUE_RETURN_BASE_WIDTHS, 150, 100],
  'Approved': [...ISSUE_RETURN_BASE_WIDTHS, 150, 100],
  'Rejected': [...ISSUE_RETURN_BASE_WIDTHS, 150, 100],
  'Canceled': [...ISSUE_RETURN_BASE_WIDTHS, 150, 100],
};

/**
 * Default column widths for Issue Return table when tab not found in config
 */
const ISSUE_RETURN_DEFAULT_WIDTHS = [...ISSUE_RETURN_BASE_WIDTHS, 150, 100];

/**
 * Get skeleton column widths for Issue Return table
 * 
 * @param selectedTab - Currently selected tab (All, Draft, Pending, etc.)
 * @param hasCheckbox - Whether the table has a checkbox column (not used currently)
 * @returns Array of column widths for skeleton rendering
 */
export const getIssueReturnSkeletonColumnWidths = createSkeletonColumnWidths(
  ISSUE_RETURN_TAB_CONFIG,
  ISSUE_RETURN_DEFAULT_WIDTHS
);

// ============================================================================
// RECEIVE RETURN SKELETON CONFIG
// ============================================================================

/**
 * Tab-specific column configurations for Receive Return skeleton
 * 
 * Note: Receive Return uses status chips (pending/received) instead of tabs
 * Column structure differs by status:
 * - pending: issue_return_no, date, total_amount, remark, actions (5 cols)
 * - received: received_return_no_date, issue_return_no_date, total_amount, remark, info_icon, print_icon (6 cols)
 */
const RECEIVE_RETURN_TAB_CONFIG: TabColumnConfig = {
  // issue_return_no, date, total_amount, remark, actions
  'pending': [140, 100, 120, 150, 80],
  // received_return_no_date, issue_return_no_date, total_amount, remark, info_icon, print_icon
  'received': [160, 160, 120, 150, 50, 50],
};

/**
 * Default column widths for Receive Return table (pending view)
 */
const RECEIVE_RETURN_DEFAULT_WIDTHS = [140, 100, 120, 150, 80];

/**
 * Get skeleton column widths for Receive Return table
 * 
 * @param statusFilter - Current status filter ('pending' or 'received')
 * @param hasCheckbox - Whether the table has a checkbox column (not used)
 * @returns Array of column widths for skeleton rendering
 */
export const getReceiveReturnSkeletonColumnWidths = createSkeletonColumnWidths(
  RECEIVE_RETURN_TAB_CONFIG,
  RECEIVE_RETURN_DEFAULT_WIDTHS
);

// ============================================================================
// EXPIRED RETURN SKELETON CONFIG
// ============================================================================

/**
 * Base column widths for Expired Return table:
 * expired_return_no, date, store, supplier, net_amount
 */
const EXPIRED_RETURN_BASE_WIDTHS = [160, 100, 160, 160, 120];

/**
 * Tab-specific column configurations for Expired Return skeleton
 * 
 * Column structure:
 * - All: expired_return_no, date, store, supplier, net_amount, status, remark, actions
 * - Others: expired_return_no, date, store, supplier, net_amount, remark, actions
 */
const EXPIRED_RETURN_TAB_CONFIG: TabColumnConfig = {
  // expired_return_no, date, store, supplier, net_amount, status, remark, actions
  'All': [...EXPIRED_RETURN_BASE_WIDTHS, 100, 150, 80],
  // expired_return_no, date, store, supplier, net_amount, remark, actions
  'Draft': [...EXPIRED_RETURN_BASE_WIDTHS, 150, 80],
  'Pending': [...EXPIRED_RETURN_BASE_WIDTHS, 150, 80],
  'Approved': [...EXPIRED_RETURN_BASE_WIDTHS, 150, 80],
  'Rejected': [...EXPIRED_RETURN_BASE_WIDTHS, 150, 80],
  'Canceled': [...EXPIRED_RETURN_BASE_WIDTHS, 150, 80],
};

/**
 * Default column widths for Expired Return table when tab not found in config
 */
const EXPIRED_RETURN_DEFAULT_WIDTHS = [...EXPIRED_RETURN_BASE_WIDTHS, 150, 80];

/**
 * Get skeleton column widths for Expired Return table
 * 
 * @param selectedTab - Currently selected tab (All, Draft, Pending, etc.)
 * @param hasCheckbox - Whether the table has a checkbox column (not used)
 * @returns Array of column widths for skeleton rendering
 */
export const getExpiredReturnSkeletonColumnWidths = createSkeletonColumnWidths(
  EXPIRED_RETURN_TAB_CONFIG,
  EXPIRED_RETURN_DEFAULT_WIDTHS
);

// ============================================================================
// SALES RETURN SKELETON CONFIG
// ============================================================================

/**
 * Base column widths for Sales Return table:
 * sales_return_no_date, patient, return_amount, paid_amount, balance_amount, status, remark, actions
 * 
 * Note: Sales Return has consistent columns across all tabs
 */
const SALES_RETURN_BASE_WIDTHS = [160, 150, 120, 120, 120, 100, 150, 80];

/**
 * Tab-specific column configurations for Sales Return skeleton
 * Note: All tabs have the same column structure
 */
const SALES_RETURN_TAB_CONFIG: TabColumnConfig = {
  'All': SALES_RETURN_BASE_WIDTHS,
  'Pending': SALES_RETURN_BASE_WIDTHS,
  'Approved': SALES_RETURN_BASE_WIDTHS,
  'Rejected': SALES_RETURN_BASE_WIDTHS,
};

/**
 * Default column widths for Sales Return table
 */
const SALES_RETURN_DEFAULT_WIDTHS = SALES_RETURN_BASE_WIDTHS;

/**
 * Get skeleton column widths for Sales Return table
 * 
 * @param selectedTab - Currently selected tab (All, Pending, Approved, Rejected)
 * @param hasCheckbox - Whether the table has a checkbox column (not used)
 * @returns Array of column widths for skeleton rendering
 */
export const getSalesReturnSkeletonColumnWidths = createSkeletonColumnWidths(
  SALES_RETURN_TAB_CONFIG,
  SALES_RETURN_DEFAULT_WIDTHS
);
